"use client";

import { useEffect } from "react";
import Image from "next/image";
import Link from "next/link";
import { usePathname } from "next/navigation";
import { ShoppingCart, X, Plus, Minus, Trash2, ArrowRight } from "lucide-react";
import { useCart, formatDkk } from "@/lib/cart/cart-context";
import type { Locale } from "@/lib/i18n/config";

/**
 * Floating cart-drawer der altid er tilgængelig på alle public-sider.
 *
 * Tre tilstande:
 *   1. Tom kurv → intet vises (drawer kan stadig åbnes via header-knap → tom UI)
 *   2. Items i kurv + lukket → flydende rund knap nede i højre hjørne med antal-badge
 *   3. Drawer åben → slide-in panel fra højre med items, totals, "Gå til betaling"
 *
 * Lægges i root layout indeni CartProvider så state deles på tværs af sider.
 */

type Props = { locale?: Locale };

export function FloatingCart({ locale: localeProp }: Props = {}) {
  // Auto-detect locale fra path hvis ikke specifikt sat
  const pathname = usePathname();
  const locale: Locale = localeProp ?? (pathname?.startsWith("/en") ? "en" : "da");

  const {
    items, itemCount, subtotal, isHydrated, isOpen,
    openCart, closeCart, removeItem, updateQuantity,
  } = useCart();

  // ESC lukker drawer
  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") closeCart();
    };
    document.addEventListener("keydown", onKey);
    // Body scroll-lock når drawer er åben
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [isOpen, closeCart]);

  // Vis intet før hydration for at undgå hydration-mismatch
  if (!isHydrated) return null;

  // Skjul kurv-UI på admin, login og checkout-flow (egne CTA'er der) — både DA og EN
  if (
    pathname?.startsWith("/admin") ||
    pathname?.startsWith("/login") ||
    pathname?.startsWith("/checkout") ||
    pathname?.startsWith("/en/checkout") ||
    pathname?.startsWith("/auth")
  ) {
    return null;
  }

  const tr = {
    cart:        locale === "en" ? "Cart" : "Kurv",
    empty:       locale === "en" ? "Your cart is empty" : "Din kurv er tom",
    emptyDesc:   locale === "en" ? "Start shopping to add items." : "Gå til butikken for at lægge varer i kurven.",
    goToShop:    locale === "en" ? "Go to shop" : "Gå til butik",
    subtotal:    locale === "en" ? "Subtotal" : "Subtotal",
    vatIncl:     locale === "en" ? "VAT incl." : "Inkl. moms",
    checkout:    locale === "en" ? "Go to checkout" : "Gå til betaling",
    viewFull:    locale === "en" ? "View full cart" : "Se hele kurven",
    remove:      locale === "en" ? "Remove" : "Fjern",
    items:       locale === "en" ? "items" : "varer",
    item:        locale === "en" ? "item" : "vare",
  };

  const shopHref = locale === "en" ? "/en/butik" : "/butik";
  const cartHref = locale === "en" ? "/en/kurv" : "/kurv";
  const checkoutHref = locale === "en" ? "/en/checkout" : "/checkout";

  return (
    <>
      {/* Floating mini-button — vises kun når der er varer OG drawer er lukket */}
      {itemCount > 0 && !isOpen && (
        <button
          type="button"
          onClick={openCart}
          aria-label={`${tr.cart} (${itemCount} ${itemCount === 1 ? tr.item : tr.items})`}
          className="fixed bottom-5 right-5 z-[80] w-14 h-14 rounded-full bg-[var(--color-primary-700)] hover:bg-[var(--color-primary-800)] text-white shadow-[0_10px_28px_rgba(0,23,37,0.25)] flex items-center justify-center transition-all hover:-translate-y-0.5 hover:shadow-[0_14px_34px_rgba(0,23,37,0.32)]"
        >
          <ShoppingCart size={22} strokeWidth={2} />
          <span
            className="absolute -top-1.5 -right-1.5 min-w-[22px] h-[22px] px-1.5 rounded-full bg-[var(--color-gold)] text-[var(--color-navy-700)] text-[11px] font-bold flex items-center justify-center border-2 border-white"
            aria-hidden="true"
          >
            {itemCount}
          </span>
        </button>
      )}

      {/* Backdrop */}
      {isOpen && (
        <div
          className="fixed inset-0 z-[90] bg-black/40 backdrop-blur-sm animate-in fade-in duration-200"
          onClick={closeCart}
          aria-hidden="true"
        />
      )}

      {/* Drawer */}
      <aside
        className={`fixed top-0 right-0 z-[91] h-[100dvh] w-full sm:w-[420px] bg-white shadow-[-12px_0_40px_rgba(0,23,37,0.18)] flex flex-col transition-transform duration-300 ease-out ${
          isOpen ? "translate-x-0" : "translate-x-full"
        }`}
        aria-hidden={!isOpen}
        inert={!isOpen}
        aria-label={tr.cart}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-[var(--color-line)]">
          <div className="flex items-center gap-2.5">
            <ShoppingCart size={18} strokeWidth={2} className="text-[var(--color-primary-700)]" />
            <h2 className="font-[family-name:var(--font-display-loaded)] text-[17px] font-bold text-[var(--color-navy-700)]">
              {tr.cart}
              {itemCount > 0 && (
                <span className="text-[var(--color-muted)] font-normal ml-1.5 text-[14px]">
                  ({itemCount})
                </span>
              )}
            </h2>
          </div>
          <button
            type="button"
            onClick={closeCart}
            aria-label={locale === "en" ? "Close" : "Luk"}
            className="p-2 rounded-[8px] text-[var(--color-muted)] hover:bg-[var(--color-surface-100)] hover:text-[var(--color-navy-700)] transition-colors"
          >
            <X size={18} strokeWidth={2} />
          </button>
        </div>

        {/* Items list — scrollable */}
        <div className="flex-1 overflow-y-auto">
          {items.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full px-6 text-center py-10">
              <div className="w-16 h-16 rounded-full bg-[var(--color-surface-100)] flex items-center justify-center mb-4">
                <ShoppingCart size={28} strokeWidth={1.5} className="text-[var(--color-muted)]" />
              </div>
              <h3 className="font-[family-name:var(--font-display-loaded)] text-[18px] font-bold text-[var(--color-navy-700)] mb-1.5">
                {tr.empty}
              </h3>
              <p className="text-[13.5px] text-[var(--color-muted)] mb-5 max-w-[260px]">
                {tr.emptyDesc}
              </p>
              <Link
                href={shopHref}
                onClick={closeCart}
                className="btn btn-primary !py-2.5"
              >
                {tr.goToShop}
              </Link>
            </div>
          ) : (
            <ul className="divide-y divide-[var(--color-line-soft)]">
              {items.map((item) => (
                <li key={item.product_id} className="p-4 flex gap-3">
                  {/* Thumb */}
                  <div className="relative w-[64px] h-[64px] flex-shrink-0 rounded-[8px] overflow-hidden bg-[var(--color-surface-100)]">
                    {item.image_url ? (
                      <Image
                        src={item.image_url}
                        alt={item.image_alt ?? item.name}
                        fill
                        className="object-cover"
                        sizes="64px"
                        unoptimized
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-[var(--color-muted)]">
                        <ShoppingCart size={20} strokeWidth={1.5} />
                      </div>
                    )}
                  </div>

                  {/* Detaljer */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-2">
                      <div className="font-semibold text-[13.5px] text-[var(--color-navy-700)] leading-[1.3]">
                        {item.name}
                      </div>
                      <button
                        type="button"
                        onClick={() => removeItem(item.product_id)}
                        aria-label={`${tr.remove} ${item.name}`}
                        className="p-1 rounded-[6px] text-[var(--color-muted)] hover:bg-[#fef2f2] hover:text-[#991b1b] transition-colors flex-shrink-0"
                      >
                        <Trash2 size={14} strokeWidth={2} />
                      </button>
                    </div>

                    <div className="flex items-center justify-between mt-2">
                      {/* Qty controls */}
                      <div className="inline-flex items-center bg-white border border-[var(--color-line)] rounded-[8px] overflow-hidden">
                        <button
                          type="button"
                          onClick={() => updateQuantity(item.product_id, item.quantity - 1)}
                          aria-label={locale === "en" ? "Decrease" : "Færre"}
                          className="px-2 py-1.5 text-[var(--color-muted)] hover:text-[var(--color-navy-700)]"
                        >
                          <Minus size={12} strokeWidth={2.5} />
                        </button>
                        <span className="px-2 text-[13px] font-bold tabular-nums min-w-[28px] text-center">
                          {item.quantity}
                        </span>
                        <button
                          type="button"
                          onClick={() => updateQuantity(item.product_id, item.quantity + 1)}
                          aria-label={locale === "en" ? "Increase" : "Flere"}
                          className="px-2 py-1.5 text-[var(--color-muted)] hover:text-[var(--color-navy-700)]"
                        >
                          <Plus size={12} strokeWidth={2.5} />
                        </button>
                      </div>

                      <div className="font-[family-name:var(--font-display-loaded)] font-bold text-[14px] text-[var(--color-navy-700)] tabular-nums">
                        {formatDkk(item.price_dkk * item.quantity, locale)}
                      </div>
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* Footer — totals + CTA */}
        {items.length > 0 && (
          <div className="border-t border-[var(--color-line)] p-5 space-y-3 bg-[var(--color-surface-50)]">
            <div className="flex items-end justify-between">
              <div>
                <div className="text-[11px] uppercase tracking-[0.08em] text-[var(--color-muted)] font-bold">
                  {tr.subtotal}
                </div>
                <div className="text-[11px] text-[var(--color-muted)]">{tr.vatIncl}</div>
              </div>
              <div className="font-[family-name:var(--font-display-loaded)] text-[22px] font-bold text-[var(--color-navy-700)] tabular-nums">
                {formatDkk(subtotal, locale)}
              </div>
            </div>

            <Link
              href={checkoutHref}
              onClick={closeCart}
              className="btn btn-primary !py-3 w-full inline-flex items-center justify-center gap-2 text-[15px]"
            >
              {tr.checkout}
              <ArrowRight size={16} strokeWidth={2.5} />
            </Link>

            <Link
              href={cartHref}
              onClick={closeCart}
              className="text-center block text-[13px] text-[var(--color-primary-700)] hover:underline font-semibold"
            >
              {tr.viewFull}
            </Link>
          </div>
        )}
      </aside>
    </>
  );
}
